import React, { useEffect, useState } from "react";
import { CheckCircle, ArrowCounterClockwise, Bug, ChatCircleDots, PaperPlaneTilt, Star } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

function PilotDigestCard() {
  const [enabled, setEnabled] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/settings").then(({ data }) => setEnabled(data.pilot_digest_enabled !== false)).catch(() => setEnabled(true));
  }, []);

  const toggle = async (v) => {
    setEnabled(v);
    try { await api.patch("/admin/settings", { pilot_digest_enabled: v }); }
    catch (e) { toast.error(formatApiError(e)); setEnabled(!v); }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/admin/pilot-digest/send-now");
      if (data.sent) toast.success(`Digest sent — ${data.logins} logins, ${data.lessons} lessons, ${data.feedback} feedback`);
      else toast.error(`Not sent: ${data.reason}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  return (
    <div className="rounded-xl border border-[#C4C8CE] bg-[#FAFAFA] px-4 py-3 mb-4 flex items-center justify-between gap-3 flex-wrap" data-testid="pilot-digest-card">
      <div>
        <div className="text-sm font-bold">Daily pilot digest</div>
        <div className="text-xs text-[#000000]">Emails super admins each day: logins, lessons completed, feedback received.</div>
      </div>
      <div className="flex items-center gap-3">
        {enabled !== null && <Switch checked={enabled} onCheckedChange={toggle} data-testid="pilot-digest-toggle" />}
        <button className="btn-secondary text-xs flex items-center gap-1" disabled={sending} onClick={sendNow} data-testid="pilot-digest-send-now">
          <PaperPlaneTilt size={13} weight="bold" /> {sending ? "Sending…" : "Send now"}
        </button>
      </div>
    </div>
  );
}

function Stars({ value, size = 14 }) {
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} size={size} weight={n <= Math.round(value) ? "fill" : "regular"} color={n <= Math.round(value) ? "#FBBC04" : "#C4C8CE"} />
      ))}
    </span>
  );
}

export default function FeedbackPanel() {
  const [items, setItems] = useState(null);
  const [ratingAvg, setRatingAvg] = useState(null);
  const [ratingCount, setRatingCount] = useState(0);
  const [filter, setFilter] = useState("all");

  const load = () => api.get("/admin/feedback").then(({ data }) => {
    setItems(data.items || []);
    setRatingAvg(data.rating_avg ?? null);
    setRatingCount(data.rating_count || 0);
  }).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try { await api.patch(`/admin/feedback/${id}`, { status }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const [drafts, setDrafts] = useState({});
  const [sendingReply, setSendingReply] = useState(null);
  const [awarding, setAwarding] = useState(null);
  const awardBonus = async (id) => {
    setAwarding(id);
    try {
      await api.post(`/admin/feedback/${id}/bug-bonus`);
      toast.success("Bug bonus awarded — +250 coins and the Pilot Tester badge");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setAwarding(null); }
  };
  const sendReply = async (id) => {
    const message = (drafts[id] || "").trim();
    if (!message) return;
    setSendingReply(id);
    try {
      await api.post(`/admin/feedback/${id}/reply`, { message });
      toast.success("Reply sent — they'll see it in their notifications");
      setDrafts((d) => ({ ...d, [id]: "" }));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSendingReply(null); }
  };

  if (items === null) return <div className="text-[#000000]">Loading…</div>;

  const shown = items.filter((f) =>
    filter === "active" ? f.status === "new" : filter === "resolved" ? f.status === "resolved" : true
  );

  return (
    <div className="card" data-testid="feedback-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-1">
        <h2 className="text-xl font-bold font-display">Pilot feedback ({shown.length}{filter !== "all" ? ` of ${items.length}` : ""})</h2>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-40 h-8 text-xs" data-testid="feedback-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all" data-testid="feedback-filter-all">Show all</SelectItem>
            <SelectItem value="active" data-testid="feedback-filter-active">Active chats</SelectItem>
            <SelectItem value="resolved" data-testid="feedback-filter-resolved">Resolved</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <p className="text-sm text-[#000000] mb-4">Everything users send from the feedback button in the header.</p>
      <PilotDigestCard />
      {ratingCount > 0 && (
        <div className="rounded-xl border border-[#C4C8CE] bg-white px-4 py-3 mb-4 flex items-center gap-4" data-testid="feedback-rating-summary">
          <div className="text-4xl font-display font-extrabold text-[#000000]" data-testid="feedback-rating-avg">{ratingAvg}</div>
          <div>
            <Stars value={ratingAvg} size={18} />
            <div className="text-xs text-[#000000] mt-0.5" data-testid="feedback-rating-count">{ratingCount} rating{ratingCount !== 1 ? "s" : ""}</div>
          </div>
        </div>
      )}
      {items.length === 0 && (
        <div className="text-[#000000] text-sm italic flex items-center gap-2" data-testid="feedback-empty">
          <ChatCircleDots size={16} /> No feedback yet.
        </div>
      )}
      <div className="flex flex-col gap-3">
        {shown.length === 0 && items.length > 0 && (
          <div className="text-[#000000] text-sm italic" data-testid="feedback-filter-empty">Nothing matches this filter.</div>
        )}
        {shown.map((f) => (
          <div key={f.id} data-testid={`feedback-item-${f.id}`}
            className={`rounded-xl border px-4 py-3 ${f.status === "resolved" ? "border-[#C4C8CE] bg-[#F8F8F8] opacity-70" : "border-[#A3E635] bg-[rgba(204,255,0,0.28)]"}`}>
            <div className="flex items-center gap-2 flex-wrap text-xs text-[#000000] mb-1">
              <b className="text-[#000000]">{f.name}</b>
              {f.email && <span>· {f.email}</span>}
              {f.role && <span className="uppercase tracking-wider">· {String(f.role).replace(/_/g, " ")}</span>}
              {f.page && <span className="font-mono">· {f.page}</span>}
              <span>· {new Date(f.at).toLocaleString()}</span>
              {f.status === "resolved" && <span className="text-[#3F6212] font-bold">RESOLVED</span>}
            </div>
            {f.rating && (
              <div className="mb-1" data-testid={`feedback-item-rating-${f.id}`}><Stars value={f.rating} /></div>
            )}
            <div className="text-sm text-[#000000] whitespace-pre-wrap mb-2">{f.message}</div>
            {(f.replies || []).map((r) => (
              <div key={r.id} className={`ml-3 border-l-2 pl-3 py-1 mb-2 ${r.from_user ? "border-[#C4C8CE]" : "border-[#00B8D4]"}`} data-testid={`feedback-reply-${r.id}`}>
                <div className="text-[11px] text-[#000000]"><b className={r.from_user ? "text-[#000000]" : "text-[#00768a]"}>{r.from_user ? `${r.admin_name} (them)` : r.admin_name}</b> · {new Date(r.at).toLocaleString()}</div>
                <div className="text-sm text-[#000000] whitespace-pre-wrap">{r.message}</div>
              </div>
            ))}
            <form className="flex gap-2 mb-2" onSubmit={(e) => { e.preventDefault(); sendReply(f.id); }}>
              <input
                className="input-base flex-1 text-sm py-1.5"
                placeholder={`Reply to ${(f.name || "them").split(" ")[0]}…`}
                value={drafts[f.id] || ""}
                onChange={(e) => setDrafts((d) => ({ ...d, [f.id]: e.target.value }))}
                maxLength={2000}
                data-testid={`feedback-reply-input-${f.id}`}
              />
              <button type="submit" className="btn-secondary text-xs flex items-center gap-1"
                disabled={sendingReply === f.id || !(drafts[f.id] || "").trim()}
                data-testid={`feedback-reply-send-${f.id}`}>
                <PaperPlaneTilt size={13} weight="bold" /> {sendingReply === f.id ? "Sending…" : "Reply"}
              </button>
            </form>
            <div className="flex items-center gap-2 flex-wrap">
              {f.status === "new" ? (
                <button data-testid={`feedback-resolve-${f.id}`} className="btn-secondary text-xs flex items-center gap-1" onClick={() => setStatus(f.id, "resolved")}>
                  <CheckCircle size={13} weight="bold" /> Mark resolved
                </button>
              ) : (
                <button data-testid={`feedback-reopen-${f.id}`} className="btn-ghost text-xs flex items-center gap-1" onClick={() => setStatus(f.id, "new")}>
                  <ArrowCounterClockwise size={13} /> Reopen
                </button>
              )}
              {f.user_id && (f.bug_bonus ? (
                <span className="text-[11px] font-bold text-[#B45309] flex items-center gap-1" data-testid={`feedback-bonus-awarded-${f.id}`}>
                  <Bug size={13} weight="fill" /> Bug bonus awarded (+{f.bug_bonus.coins})
                </span>
              ) : (
                <button data-testid={`feedback-bug-bonus-${f.id}`} className="btn-ghost text-xs flex items-center gap-1 text-[#B45309]"
                  disabled={awarding === f.id} onClick={() => awardBonus(f.id)}
                  title="Confirms a bug catch: gives them +250 coins and the Pilot Tester badge">
                  <Bug size={13} weight="bold" /> {awarding === f.id ? "Awarding…" : "Bug bonus +250"}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
